import React from 'react';
import { Trash2, X, AlertTriangle } from 'lucide-react';
import { UserPermissionProfile } from '../../services/userManagementService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: UserPermissionProfile | null;
  onConfirm: () => void;
  isDeleting: boolean;
  error: string | null;
}

export const ConfirmDeleteUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  isDeleting,
  error
}) => {
  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden text-left animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabeçalho */}
        <div className="p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-rose-600/20 text-rose-400 flex items-center justify-center border border-rose-500/30">
              <Trash2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black text-white">Excluir Usuário</h2>
              <p className="text-xs text-slate-400">Esta ação não pode ser desfeita</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-5 space-y-4 text-xs">
          {error && (
            <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-300 font-bold">
              {error}
            </div>
          )}

          <div className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 space-y-2">
            <div className="text-slate-300">
              Você está prestes a remover o seguinte usuário:
            </div>
            <div className="font-bold text-white text-sm">
              {user.name} <span className="text-slate-400 font-normal">(@{user.login})</span>
            </div>
            <div className="text-slate-400">
              E-mail: <span className="text-slate-200 font-mono">{user.email || 'Não informado'}</span>
            </div>
            {user.auth_user_id && (
              <div className="text-slate-400">
                UUID Supabase Auth: <span className="text-indigo-300 font-mono">{user.auth_user_id}</span>
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-950/30 border border-amber-800/50 text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex flex-col space-y-1">
              <span className="font-bold">Recomendação de Segurança:</span>
              <span>
                Para usuários que já operaram ordens de serviço, medições ou lançamentos financeiros, a melhor prática é alterar o status para <strong>Inativo</strong>. Isso bloqueia o acesso instantaneamente e mantém o histórico e a rastreabilidade das operações passadas.
              </span>
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-950/30 border border-rose-800/50 text-rose-300 mt-2">
            <Trash2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              A exclusão desvinculará o usuário de todos os grupos e permissões permanentemente.
            </span>
          </div>

          {/* Botões */}
          <div className="pt-3 border-t border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="px-5 py-2.5 rounded-xl bg-rose-950/50 hover:bg-rose-900 border border-rose-800 text-rose-400 text-xs font-bold flex items-center justify-center gap-2 transition disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? 'Excluindo...' : 'Sim, Excluir Mesmo Assim'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
